import React, { useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import BillingAccountsCard from './BillingAccountsCard';
import BillingActivitiesTab from './BillingActivitiesTab';
import { listBillingRates, setBillingRate, generateInvoices, updateInvoicePaid, updateInvoiceGrace, updateInvoiceManualBalance } from '../../api/billing';
import { updateCompany } from '../../api/superadmin';
import { todayISO } from '../../utils/date';
import './SuperadminBillingPage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return `${d}/${m}/${y}`;
};


const currentMonthISO = () => todayISO().slice(0, 7);

const monthPeriod = (month) => {
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return {
    periodStart: `${month}-01`,
    periodEnd: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
};


const SuperadminBillingPage = () => {
  const [activeTab, setActiveTab] = useState('billing');
  const [rates, setRates] = useState([]);
  const [rateDrafts, setRateDrafts] = useState({});
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState('');
  const [savingRateId, setSavingRateId] = useState(null);

  const [billingMonth, setBillingMonth] = useState(currentMonthISO());
  const [invoices, setInvoices] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [busyInvoiceId, setBusyInvoiceId] = useState(null);
  const [paidDrafts, setPaidDrafts] = useState({});
  const [graceDrafts, setGraceDrafts] = useState({});
  const [manualDrafts, setManualDrafts] = useState({});

  const loadRates = () => {
    setRatesLoading(true);
    setRatesError('');
    listBillingRates()
      .then((data) => {
        setRates(data);
        const drafts = {};
        data.forEach((r) => { drafts[r.company_id] = r.amount === null ? '' : String(r.amount); });
        setRateDrafts(drafts);
      })
      .catch(() => setRatesError('Impossible de charger les tarifs.'))
      .finally(() => setRatesLoading(false));
  };

  useEffect(() => { loadRates(); }, []);

  const saveRate = async (companyId) => {
    const value = rateDrafts[companyId];
    if (value === '' || value === undefined || Number.isNaN(Number(value))) {
      setRatesError('Tarif invalide.');
      return;
    }
    setSavingRateId(companyId);
    setRatesError('');
    try {
      const updated = await setBillingRate(companyId, value);
      setRates((prev) => prev.map((r) => (r.company_id === companyId ? updated : r)));
    } catch (err) {
      setRatesError('Sauvegarde du tarif impossible.');
    } finally {
      setSavingRateId(null);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!billingMonth || !/^\d{4}-\d{2}$/.test(billingMonth)) {
      setReportError('Veuillez sélectionner un mois valide.');
      return;
    }
    const { periodStart, periodEnd } = monthPeriod(billingMonth);
    if (periodEnd > todayISO()) {
      setReportError('Le mois sélectionné doit être entièrement terminé.');
      return;
    }
    setReportLoading(true);
    setReportError('');
    try {
      const data = await generateInvoices({ periodStart, periodEnd });
      setInvoices(data);
      const drafts = {};
      const grace = {};
      const manual = {};
      data.forEach((inv) => {
        drafts[inv.id] = String(inv.amount_paid);
        grace[inv.id] = String(inv.grace_days);
        manual[inv.id] = String(inv.manual_balance ?? 0);
      });
      setPaidDrafts(drafts);
      setGraceDrafts(grace);
      setManualDrafts(manual);
    } catch (err) {
      let detail = '';
      try { detail = JSON.parse(err.message).detail || ''; } catch (parseErr) { /* message non-JSON */ }
      setReportError(detail || 'Génération du rapport impossible.');
      setInvoices(null);
    } finally {
      setReportLoading(false);
    }
  };

  const savePaid = async (invoice) => {
    const value = paidDrafts[invoice.id];
    if (value === '' || value === undefined || Number.isNaN(Number(value))) {
      setReportError('Montant payé invalide.');
      return;
    }
    setBusyInvoiceId(invoice.id);
    setReportError('');
    try {
      const updated = await updateInvoicePaid(invoice.id, value);
      setInvoices((prev) => prev.map((i) => (i.id === invoice.id ? updated : i)));
    } catch (err) {
      setReportError('Sauvegarde du montant payé impossible.');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const saveGrace = async (invoice) => {
    const value = graceDrafts[invoice.id];
    if (value === '' || value === undefined || Number.isNaN(Number(value)) || Number(value) < 0) {
      setReportError('Délai (jours) invalide.');
      return;
    }
    setBusyInvoiceId(invoice.id);
    setReportError('');
    try {
      const updated = await updateInvoiceGrace(invoice.id, value);
      setInvoices((prev) => prev.map((i) => (i.id === invoice.id ? updated : i)));
    } catch (err) {
      setReportError('Sauvegarde du délai impossible.');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const saveManualBalance = async (invoice) => {
    const value = manualDrafts[invoice.id];
    if (value === '' || value === undefined || Number.isNaN(Number(value))) {
      setReportError('Solde manuel invalide.');
      return;
    }
    setBusyInvoiceId(invoice.id);
    setReportError('');
    try {
      const updated = await updateInvoiceManualBalance(invoice.id, value);
      setInvoices((prev) => prev.map((i) => (i.id === invoice.id ? updated : i)));
      setManualDrafts((prev) => ({ ...prev, [invoice.id]: String(updated.manual_balance ?? 0) }));
    } catch (err) {
      setReportError('Sauvegarde du solde manuel impossible.');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const toggleBlocked = async (invoice) => {
    setBusyInvoiceId(invoice.id);
    setReportError('');
    try {
      await updateCompany(invoice.company, { billing_blocked: !invoice.billing_blocked });
      setInvoices((prev) => prev.map((i) => (
        i.company === invoice.company ? { ...i, billing_blocked: !invoice.billing_blocked } : i
      )));
    } catch (err) {
      setReportError('Action impossible sur cette company.');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  return (
    <SuperadminLayout>
      <h2 className="sb-title">Facturation</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <button type="button" className={activeTab === 'billing' ? 'sb-save-btn' : 'sb-block-btn'} onClick={() => setActiveTab('billing')}>
          Facturation
        </button>
        <button type="button" className={activeTab === 'activities' ? 'sb-save-btn' : 'sb-block-btn'} onClick={() => setActiveTab('activities')}>
          Annuler / Supprimer
        </button>
      </div>
      {activeTab === 'activities' ? <BillingActivitiesTab /> : <>
      <div className="sb-card">
        <div className="sb-card-header">
          <span className="sb-card-title">Tarif par POS</span>
        </div>
        <div className="sb-card-body">
          {ratesError && <p className="sb-error">{ratesError}</p>}
          {ratesLoading ? (
            <p>Chargement...</p>
          ) : (
            <div className="sb-table-wrap">
            <table className="sb-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Banks</th>
                  <th>Tarif / bank</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.company_id}>
                    <td>{r.company_name}</td>
                    <td className="sb-center">{r.bank_count}</td>
                    <td>
                      <input
                        className="sb-input sb-input-sm"
                        type="text"
                        inputMode="decimal"
                        value={rateDrafts[r.company_id] ?? ''}
                        onChange={(e) => setRateDrafts((prev) => ({ ...prev, [r.company_id]: e.target.value }))}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="sb-save-btn"
                        disabled={savingRateId === r.company_id}
                        onClick={() => saveRate(r.company_id)}
                      >
                        Sauver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <BillingAccountsCard />

      <div className="sb-card">
        <div className="sb-card-header">
          <span className="sb-card-title">Rapport de facturation</span>
        </div>
        <div className="sb-card-body">
          <form onSubmit={handleGenerate} className="sb-form">
            <div className="sb-row">
            <label className="sb-label" htmlFor="billing-month">Mois</label>
            <input
              id="billing-month"
              className="sb-input sb-input-date"
              type="month"
              value={billingMonth}
              onChange={(e) => setBillingMonth(e.target.value)}
              required
            />
          </div>
          <p className="sb-hint">
            Sélectionnez un mois calendaire terminé. Une POS est facturable si elle atteint 5 nouveaux tickets valides et non supprimés pendant ce mois.
          </p>
            <p className="sb-hint">
              Blocage manuel uniquement : le blocage automatique pour impayé est suspendu.
              Le Superadmin conserve le contrôle pour bloquer ou débloquer chaque company manuellement.
            </p>
            {reportError && <p className="sb-error">{reportError}</p>}
            <button type="submit" className="sb-generate-btn" disabled={reportLoading}>
              {reportLoading ? 'Génération...' : 'Générer'}
            </button>
          </form>

          {invoices && (
            <div className="sb-table-wrap">
            <table className="sb-table sb-results">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>POS total</th>
                  <th>POS facturables</th>
                  <th>Tarif</th>
                  <th>Montant période</th>
                  <th>Ancien solde reporté</th>
                    <th>Solde manuel</th>
                    <th>Total dû</th>
                  <th>Montant payé</th>
                  <th>Solde</th>
                  <th>Délai limite</th>
                  <th>+ jours de grâce</th>
                  <th>Bloqué (impayé)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={14} className="sb-empty">Aucune company avec un tarif configuré.</td></tr>
                )}
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.company_name}</td>
                    <td className="sb-center">{inv.total_bank_count}</td>
                    <td className="sb-center">{inv.billable_bank_count}</td>
                    <td className="sb-money">{money(inv.rate_per_bank)}</td>
                    <td className="sb-money">{money(inv.amount_due)}</td>
                    <td className="sb-money">{money(inv.carried_balance)}</td>
                    <td>
                      <input
                        className="sb-input sb-input-sm"
                        type="text"
                        inputMode="decimal"
                        value={manualDrafts[inv.id] ?? ''}
                        onChange={(e) => setManualDrafts((prev) => ({ ...prev, [inv.id]: e.target.value }))}
                        onBlur={() => saveManualBalance(inv)}
                        disabled={busyInvoiceId === inv.id}
                      />
                    </td>
                    <td className="sb-money">{money(inv.total_due)}</td>
                    <td>
                      <input
                        className="sb-input sb-input-sm"
                        type="text"
                        inputMode="decimal"
                        value={paidDrafts[inv.id] ?? ''}
                        onChange={(e) => setPaidDrafts((prev) => ({ ...prev, [inv.id]: e.target.value }))}
                        onBlur={() => savePaid(inv)}
                        disabled={busyInvoiceId === inv.id}
                      />
                    </td>
                    <td className={`sb-money ${inv.balance > 0 ? 'sb-balance-due' : 'sb-balance-clear'}`}>
                      {money(inv.balance)}
                    </td>
                    <td className={`sb-center ${inv.is_overdue ? 'sb-balance-due' : ''}`}>
                      {fmtDate(inv.effective_due_date)}
                    </td>
                    <td>
                      <input
                        className="sb-input sb-input-sm"
                        type="text"
                        inputMode="numeric"
                        value={graceDrafts[inv.id] ?? ''}
                        onChange={(e) => setGraceDrafts((prev) => ({ ...prev, [inv.id]: e.target.value }))}
                        onBlur={() => saveGrace(inv)}
                        disabled={busyInvoiceId === inv.id}
                      />
                    </td>
                    <td className="sb-center">{inv.billing_blocked ? 'Oui' : 'Non'}</td>
                    <td>
                      <button
                        type="button"
                        className={inv.billing_blocked ? 'sb-unblock-btn' : 'sb-block-btn'}
                        disabled={busyInvoiceId === inv.id}
                        onClick={() => toggleBlocked(inv)}
                      >
                        {inv.billing_blocked ? 'Débloquer' : 'Bloquer'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
      </>}
    </SuperadminLayout>
  );
};

export default SuperadminBillingPage;
